import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

// Pusher closes the socket for good on these (4004 = account over connection quota,
// 4100 = over capacity). Retry later instead of staying disconnected until a reload.
const RETRYABLE_ERROR_CODES = new Set([4004, 4100])
const RETRY_DELAYS_MS = [15000, 30000, 60000, 120000]

let retryTimer = null
let retryAttempt = 0

const createEcho = (token) => {
    const echo = new Echo({
        broadcaster: 'pusher',
        key: process.env.VUE_APP_PUSHER_KEY,
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        forceTLS: true,
        authEndpoint: process.env.VUE_APP_AUTH_ENDPOINT,
        auth: {
            headers: {
                Authorization: 'Bearer ' + token
            }
        }
    })
    echo.authToken = token

    const pusher = echo.connector.pusher
    pusher.connection.bind('error', (event) => {
        const code = event?.error?.data?.code
        if (!RETRYABLE_ERROR_CODES.has(code) || window.Echo !== echo) return

        console.error(`Pusher refused the connection (${code}): ${event.error.data.message}`)
        clearTimeout(retryTimer)
        const delay = RETRY_DELAYS_MS[Math.min(retryAttempt, RETRY_DELAYS_MS.length - 1)]
        retryAttempt++
        retryTimer = setTimeout(() => {
            if (window.Echo === echo && pusher.connection.state === 'disconnected') pusher.connect()
        }, delay)
    })
    pusher.connection.bind('connected', () => {
        retryAttempt = 0
    })

    return echo
}

/**
 * One Pusher connection per tab. The default layout is created again after every
 * login / layout switch; creating a new Echo each time leaked a WebSocket per switch
 * (and used up the Pusher connection quota).
 */
export const setupEcho = () => {
    window.Pusher = Pusher
    if (process.env.NODE_ENV === 'development') {
        Pusher.logToConsole = true
    }

    const token = localStorage.getItem('userToken')
    if (window.Echo?.authToken === token) {
        return window.Echo
    }

    // logged in as someone else (or the token changed): private channels need the new token
    teardownEcho()
    window.Echo = createEcho(token)

    return window.Echo
}

export const teardownEcho = () => {
    clearTimeout(retryTimer)
    retryAttempt = 0
    if (window.Echo) {
        try {
            window.Echo.disconnect()
        } catch (error) {
            console.error('Failed to disconnect Echo:', error)
        }
        window.Echo = null
    }
}
